import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
  Balance,
  Contact,
  CurrentStep,
  Goals,
  LocalStorageKey,
  Profile,
  Result,
  What,
  Who,
  Field,
  ExtraCheckedFields,
} from '../types';
import {
  getActiva,
  getCurrentStep,
  getPassiva,
  validations,
} from '../../helpers';
import config from '../context/config/config';

type PossibleProfile = Who | What | Goals | Contact | Profile;

export interface ProfileContextProps {
  onChange: (
    currentStep: keyof Profile,
  ) => (
    data: Partial<Who> | Partial<What> | Partial<Goals> | Partial<Contact>,
  ) => void;
  useProfile: <T extends PossibleProfile>(currentStep?: keyof Profile) => T;
  useCurrentStep: () => { currentStep: CurrentStep };
  useBalanceSheet: () => Balance;
  useCurrentStepHasAllFields: (
    currentStep: keyof Profile,
  ) => { currentStepHasAllFields: boolean };
  fieldError: any;
  setExistingProfile: (profile: any) => void;
}

export const ProfileContext = React.createContext<ProfileContextProps | null>(
  null,
);
ProfileContext.displayName = 'PROFILECONTEXT';

export const useProfileContext = () => {
  const ctx = useContext(ProfileContext);
  if (!ctx) {
    throw new Error('Wrap app with AuthProvider');
  }
  return ctx;
};

interface Props {
  children: React.ReactNode;
}

export const ProfileProvider: React.FC<Props> = ({ children }: Props) => {
  const localStorageProfile = JSON.parse(
    localStorage.getItem(LocalStorageKey.PROFILE) as string,
  );

  const [profile, setProfile] = useState<Profile>(localStorageProfile || {});

  const constructedProfile = useMemo(
    (): any => ({
      who: new Who(profile.who),
      what: new What(profile.what),
      goals: new Goals(profile.goals),
      contact: new Contact(profile.contact),
      result: profile.result ? new Result(profile.result) : undefined,
    }),
    [profile],
  );

  const useProfile = <T extends PossibleProfile>(currentStep?: keyof Profile) =>
    currentStep
      ? (constructedProfile[currentStep] as T)
      : (constructedProfile as T);

  const useCurrentStep = () => ({
    currentStep: getCurrentStep(constructedProfile),
  });

  const useBalanceSheet = () => ({
    activa: getActiva(profile.what),
    passiva: getPassiva(profile.what),
  });

  // TODO: eventually I want to get rid of handling isComplete in the class
  const useCurrentStepHasAllFields = (currentStep: keyof Profile) => {
    if (currentStep === 'who') {
      if (!profile.who) {
        return {
          currentStepHasAllFields: false,
        };
      }

      let isEveryFieldValid = config.whoFields.every((whoField: keyof Who) => {
        return validations[whoField].every((validation: any) => {
          return validation.validation(profile.who[whoField]);
        });
      });

      // when we have result, do extra checks on new fields on WHO
      if (profile.result) {
        const extraCheckedFields: ExtraCheckedFields[] = ['email', 'mobile'];
        const extraFieldsValid = extraCheckedFields.every(
          (extraField: ExtraCheckedFields) => {
            return validations[extraField].every((validation: any) => {
              return validation.validation(profile.contact[extraField]);
            });
          },
        );

        if (!extraFieldsValid) isEveryFieldValid = false;
      }

      return {
        currentStepHasAllFields: isEveryFieldValid,
      };
    }

    return {
      currentStepHasAllFields:
        constructedProfile[currentStep]?.isCompleted ?? false,
    };
  };

  const setExistingProfile = (profile: any) => {
    setProfile(profile);
    localStorage.setItem(LocalStorageKey.PROFILE, JSON.stringify(profile));
  };

  const onChange = (currentStep: keyof Profile) => (
    data: Partial<Who> | Partial<What> | Partial<Goals>,
  ) => {
    const p = {
      ...profile,
      [currentStep]: { ...profile[currentStep], ...data },
    };

    if (p.result != null && currentStep !== 'result') {
      p.result.isDirty = true;
    }
    setProfile(p);
    localStorage.setItem(LocalStorageKey.PROFILE, JSON.stringify(p));
  };

  const fieldError = useCallback(
    (step: keyof Profile, field: Field) => {
      const fieldValidations: any[] = validations[field];

      if (!fieldValidations) {
        return '';
      }

      return fieldValidations.reduce(
        (acc: any, { validation: fieldIsValid, msg }: any) => {
          if (!fieldIsValid(constructedProfile[step][field])) {
            return msg;
          }
          return acc;
        },
        '',
      );
    },
    [profile],
  );

  const value = useMemo(
    () => ({
      onChange,
      useProfile,
      useCurrentStep,
      useBalanceSheet,
      useCurrentStepHasAllFields,
      setExistingProfile,
      fieldError,
    }),
    [onChange, profile, useProfile, useCurrentStep, useBalanceSheet],
  );

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
};
